import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Patient {
  id: string
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
}

interface Slide {
  id: string
  slide_id: string
  name: string
  status: string
  created_at: string
}

interface Report {
  id: string
  report_id: string
  status: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'FINALIZED'
  created_at: string
  updated_at: string
  report_type?: 'histopathology' | 'cytology'
}

interface PatientTask {
  patient: Patient
  slides: Slide[]
  reports: Report[]
  needsReport: boolean
  hasDraft: boolean
}

interface Notification {
  id: number
  notification_id: string
  subject: string
  message: string
  status: string
  created_at: string
  data: {
    case_id?: string
    patient_name?: string
    slide_name?: string
  }
}

const PathologistWorklist: React.FC = () => {
  const [tasks, setTasks] = useState<PatientTask[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filter, setFilter] = useState<'all' | 'needs_report' | 'has_draft'>('needs_report')
  const [showNotifications, setShowNotifications] = useState(false)

  useEffect(() => {
    fetchWorklist()
    fetchNotifications()
  }, [])

  const fetchWorklist = async () => {
    try {
      setIsLoading(true)

      // Fetch all data including both histopathology and cytology reports
      const [patientsRes, slidesRes, histoReportsRes, cytoReportsRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/imaging/slides/?page_size=1000'),
        fetch('/api/v1/pathology/reports/?page_size=1000'),
        fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      ])

      const patientsData = await patientsRes.json()
      const slidesData = await slidesRes.json()
      const histoReportsData = await histoReportsRes.json()
      const cytoReportsData = await cytoReportsRes.json()

      const patients = patientsData.results || patientsData
      const slides = slidesData.results || slidesData

      // Combine both types of reports
      const histoReports = (histoReportsData.results || histoReportsData).map((r: any) => ({
        ...r,
        report_type: 'histopathology'
      }))

      const cytoReports = (cytoReportsData.results || cytoReportsData).map((r: any) => ({
        id: r.id,
        report_id: `CYT-${r.id}`,
        patient: r.patient,
        status: r.status === 'draft' ? 'DRAFT' : r.status === 'pending_review' ? 'PENDING_REVIEW' : r.status === 'finalized' ? 'APPROVED' : 'DRAFT',
        created_at: r.report_date || r.created_at,
        updated_at: r.updated_at,
        report_type: 'cytology'
      }))

      const reports = [...histoReports, ...cytoReports]

      // Group slides by patient
      const slidesByPatient = new Map<string, Slide[]>()
      slides.forEach((slide: any) => {
        // Check if slide has case and patient_id
        if (slide.case && slide.patient_id) {
          const patientId = String(slide.patient_id)
          if (!slidesByPatient.has(patientId)) {
            slidesByPatient.set(patientId, [])
          }
          slidesByPatient.get(patientId)!.push({
            id: slide.id,
            slide_id: slide.slide_id,
            name: slide.name,
            status: slide.status,
            created_at: slide.created_at
          })
        }
      })

      // Group reports by patient (ensure consistent string keys)
      const reportsByPatient = new Map<string, Report[]>()
      reports.forEach((report: any) => {
        // Handle both object and ID references
        let patientId: string
        if (typeof report.patient === 'object' && report.patient?.id) {
          patientId = String(report.patient.id)
        } else if (report.patient) {
          patientId = String(report.patient)
        } else {
          return // Skip if no patient
        }

        if (patientId && patientId !== 'undefined') {
          if (!reportsByPatient.has(patientId)) {
            reportsByPatient.set(patientId, [])
          }
          reportsByPatient.get(patientId)!.push({
            id: report.id,
            report_id: report.report_id,
            status: report.status,
            created_at: report.created_at,
            updated_at: report.updated_at,
            report_type: report.report_type
          })
        }
      })

      // Build task list
      const taskList: PatientTask[] = []
      patients.forEach((patient: Patient) => {
        const patientSlides = slidesByPatient.get(String(patient.id)) || []
        const patientReports = reportsByPatient.get(String(patient.id)) || []

        // Only include patients with slides
        if (patientSlides.length > 0) {
          const hasApprovedReport = patientReports.some(r => r.status === 'APPROVED' || r.status === 'FINALIZED')
          const hasDraft = patientReports.some(r => r.status === 'DRAFT' || r.status === 'PENDING_REVIEW')
          const needsReport = patientSlides.length > 0 && !hasApprovedReport && !hasDraft

          taskList.push({
            patient,
            slides: patientSlides,
            reports: patientReports,
            needsReport,
            hasDraft
          })
        }
      })

      // Sort by priority: needs report first, then by latest slide
      taskList.sort((a, b) => {
        // Needs report has priority
        if (a.needsReport && !b.needsReport) return -1
        if (!a.needsReport && b.needsReport) return 1

        // Then sort by latest slide
        const aLatest = a.slides[0]?.created_at || ''
        const bLatest = b.slides[0]?.created_at || ''
        return bLatest.localeCompare(aLatest)
      })

      setTasks(taskList)
    } catch (error) {
      console.error('Error fetching worklist:', error)
      toast.error('Không thể tải danh sách công việc')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchNotifications = async () => {
    try {
      const response = await fetch('/api/v1/notifications/notifications/?page_size=10')
      const data = await response.json()
      const notificationList = data.results || data
      // Only show unread notifications
      setNotifications(notificationList.filter((n: Notification) => n.status === 'PENDING'))
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  const markAsRead = async (notificationId: number) => {
    try {
      await fetch(`/api/v1/notifications/notifications/${notificationId}/mark-read/`, {
        method: 'POST'
      })
      // Remove from list
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const getTaskStatus = (task: PatientTask) => {
    if (task.reports.some(r => r.status === 'APPROVED' || r.status === 'FINALIZED')) {
      return { label: 'Đã hoàn thành', color: 'bg-green-100 text-green-800', icon: '✅' }
    }
    if (task.hasDraft) {
      return { label: 'Đang soạn', color: 'bg-blue-100 text-blue-800', icon: '📝' }
    }
    return { label: 'Cần tạo phiếu', color: 'bg-red-100 text-red-800', icon: '⚠️' }
  }

  const filteredTasks = tasks.filter(task => {
    if (filter === 'needs_report') return task.needsReport
    if (filter === 'has_draft') return task.hasDraft
    return true
  })

  const stats = {
    needsReport: tasks.filter(t => t.needsReport).length,
    hasDraft: tasks.filter(t => t.hasDraft).length,
    total: tasks.length
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📋 Worklist - Bác Sĩ / KTV</h1>
        <p className="text-gray-600 mt-1">Danh sách bệnh nhân cần tạo phiếu kết quả</p>
      </div>

      {/* Notifications - Compact */}
      {notifications.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="w-full bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-3 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">🔔</span>
              <span className="font-semibold text-blue-900">
                Thông báo mới ({notifications.length})
              </span>
            </div>
            <svg
              className={`w-5 h-5 text-blue-700 transition-transform ${showNotifications ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showNotifications && (
            <div className="mt-2 max-h-80 overflow-y-auto space-y-2 bg-gray-50 rounded-lg p-2">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="bg-white border border-blue-200 rounded-lg p-3 flex items-start justify-between hover:shadow-sm transition-shadow"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-blue-900 text-sm truncate">{notif.subject}</h3>
                    <p className="text-blue-800 text-xs mt-1 line-clamp-2">{notif.message}</p>
                    {notif.data?.patient_name && (
                      <p className="text-blue-600 text-xs mt-1">
                        {notif.data.patient_name} - {notif.data.case_id}
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(notif.created_at).toLocaleString('vi-VN', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => markAsRead(notif.id)}
                    className="ml-3 flex-shrink-0 text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50"
                  >
                    ✓
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-3xl font-bold text-red-700">{stats.needsReport}</div>
          <div className="text-sm text-red-600 mt-1">⚠️ Cần tạo phiếu</div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-3xl font-bold text-blue-700">{stats.hasDraft}</div>
          <div className="text-sm text-blue-600 mt-1">📝 Đang soạn thảo</div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-gray-700">{stats.total}</div>
          <div className="text-sm text-gray-600 mt-1">📊 Tổng số ca</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-4 flex space-x-2">
        <button
          onClick={() => setFilter('needs_report')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'needs_report'
              ? 'bg-red-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ⚠️ Cần tạo phiếu ({stats.needsReport})
        </button>
        <button
          onClick={() => setFilter('has_draft')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'has_draft'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📝 Đang soạn ({stats.hasDraft})
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'all'
              ? 'bg-gray-700 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Tất cả ({stats.total})
        </button>
      </div>

      {/* Task List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Đang tải...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">✅ Không có công việc nào</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bệnh nhân
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Số ảnh
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phiếu kết quả
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cần làm
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => {
                  const status = getTaskStatus(task)

                  return (
                    <tr key={task.patient.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {task.patient.full_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Mã BN: {task.patient.patient_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {task.slides.length} ảnh
                        </div>
                        <div className="text-xs text-gray-500">
                          {task.slides.filter(s => s.status === 'ready').length} sẵn sàng
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {task.reports.length > 0 ? `${task.reports.length} phiếu` : 'Chưa có'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                          {status.icon} {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {task.hasDraft && task.reports.length > 0 ? (
                          task.reports[0].report_type === 'cytology' ? (
                            <Link
                              to={`/pathology/cytology-reports/${task.reports[0].id}`}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              📝 Tiếp tục soạn
                            </Link>
                          ) : (
                            task.slides.length > 0 ? (
                              <Link
                                to={`/imaging/viewer/${task.slides[0].id}?editReportId=${task.reports[0].id}`}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                📝 Tiếp tục soạn
                              </Link>
                            ) : (
                              <Link
                                to={`/pathology/reports/${task.reports[0].id}`}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                📝 Tiếp tục soạn
                              </Link>
                            )
                          )
                        ) : task.needsReport && task.slides.length > 0 ? (
                          <Link
                            to={`/imaging/viewer/${task.slides[0].id}`}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            🔬 Xem ảnh & Tạo phiếu
                          </Link>
                        ) : (
                          <Link
                            to={`/pathology/patients/${task.patient.id}`}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                          >
                            👁️ Xem chi tiết
                          </Link>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default PathologistWorklist
